import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useKategoriHarga, labelKategori, labelDenganKode, kodeBawaan } from "../lib/kategoriHarga";
import { useAuth } from "../contexts/AuthContext";
import { Link } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { StatusBadge } from "../components/etams/StatusBadge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Plus, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { formatIDR } from "../lib/format";
import { useDaftar } from "../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../components/etams/PalangDaftar";
import { apiErrorMessage } from "../lib/inventoryApi";
import Hint from "../components/etams/Hint";

const KOLOM_CSV = [
  { kunci: "price_group_id", judul: "Price Group ID" },
  { kunci: "package_category", judul: "Kategori" },
  { kunci: "item_type", judul: "Tipe" },
  { kunci: "fabric", judul: "Bahan" },
  { kunci: "min_qty", judul: "Minimum pesanan / jenjang dari" },
  { kunci: "max_qty", judul: "Jenjang sampai" },
  { kunci: "unit_price_idr", judul: "Harga Satuan (IDR)" },
  { kunci: "active", judul: "Aktif", ambil: (it) => (it.active ? "Ya" : "Tidak") },
];

// U22b (22 Sep 2026): kategori harga = data — `useKategoriHarga` (label tampil, kode dikirim);
// minimum pesanan bawaan baris = `minimum_pesanan` kategori (K187 satu sumber).
// K177 2.1 (20 Sep 2026): TIGA baris tetap per paket — nol harga "celana saja".
// Cermin `services/pagar_harga.ITEM_TYPE_SAH`; server menolak nilai lain (400 ITEM_TYPE_TIDAK_SAH).
const ITEM_TYPES = ["JERSEY_SAJA", "SET_CELANA_POLOS", "SET_CELANA_FULLPRINT"];
const LABEL_TIPE = { JERSEY_SAJA: "Jersey saja", SET_CELANA_POLOS: "Jersey set celana hitam/putih", SET_CELANA_FULLPRINT: "Jersey set celana full print" };


export default function MasterPriceListItems() {
  const { isOwner, isKepalaCabang, profile } = useAuth();
  // K177 bagian 1 (20 Sep 2026) MEMBALIK kunci K169 (13 Sep, `bolehTulis = false`):
  // master harga ditulis dari layar oleh Owner (semua region) dan Kepala Cabang
  // (region SENDIRI) — SESUDAH empat pagar K169 §4 berdiri di router
  // (`services/pagar_harga.py`, 21 Sep 2026). Layar hanya menyembunyikan tombol;
  // yang menjaga tetap router (REGION_FORBIDDEN / ITEM_TYPE_TIDAK_SAH / …).
  const bolehTulis = isOwner || isKepalaCabang;
  const regionSaya = profile?.region_id || null;
  // KC hanya boleh menyentuh group yang region_ids-nya TEPAT regionnya (K177 1.1).
  const bolehTulisGroup = (g) => isOwner || (isKepalaCabang && regionSaya
    && (g?.region_ids || []).length === 1 && g.region_ids[0] === regionSaya);
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterPg, setFilterPg] = useState("ALL");
  const [filterCat, setFilterCat] = useState("ALL");
  const kategoriHarga = useKategoriHarga();
  const [filterType, setFilterType] = useState("ALL");
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterPg !== "ALL") params.price_group_id = filterPg;
      if (filterCat !== "ALL") params.package_category = filterCat;
      if (filterType !== "ALL") params.item_type = filterType;
      const [it, pg] = await Promise.all([
        api.get("/master/price-list-items", { params }),
        api.get("/master/price-groups"),
      ]);
      setItems(it.data.items || []);
      setGroups(pg.data.items || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal memuat data"));
    } finally { setLoading(false); }
  }, [filterPg, filterCat, filterType]);
  useEffect(() => { load(); }, [load]);
  const daftar = useDaftar(items, {
    kunciCari: ["package_category", "item_type", "fabric"],
    urutAwal: { kunci: "unit_price_idr", arah: "asc" },
  });

  const pgName = (id) => groups.find((g) => g.id === id)?.code || "—";

  const handleDelete = async () => {
    try {
      await api.delete(`/master/price-list-items/${confirmDel.id}`);
      toast.success("Item dihapus");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Item gagal dihapus")); }
    finally { setConfirmDel(null); }
  };

  return (
    <div className="space-y-6" data-testid="master-pli-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Daftar Harga</h1>
          <p className="text-sm text-muted-foreground mt-1">Daftar harga per Price Group · kategori paket · jenjang qty · harga satuan.{" "}
            <Link to="/master/opsi-harga" className="underline" data-testid="pli-ke-opsi">Opsi &amp; tambahan (lengan panjang, raglan)</Link></p>
        <p className="text-sm rounded-md border border-border bg-muted px-3 py-2 mt-2" data-testid="pli-aturan">
          Per cabang, per paket, <strong>tiga baris tetap</strong>: Jersey saja · Jersey set celana hitam/putih · Jersey set celana full print.{" "}
          <Hint>Nama paket dan angkanya boleh berubah; susunannya tidak. Owner mengisi semua cabang; Kepala Cabang hanya cabangnya sendiri.</Hint>
        </p>
        </div>
        {bolehTulis && (
          <Button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="pli-new-btn">
            <Plus size={16} className="mr-1" /> Tambah Item
          </Button>
        )}
      </div>

      <Card className="p-4">
        <div className="flex flex-wrap gap-3 mb-4">
          <Select value={filterPg} onValueChange={setFilterPg}>
            <SelectTrigger className="w-56" data-testid="pli-filter-pg"><SelectValue placeholder="Price Group" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Semua Price Group</SelectItem>
              {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.code} — {g.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterCat} onValueChange={setFilterCat}>
            <SelectTrigger className="w-40"><SelectValue placeholder="Kategori" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Semua Kategori</SelectItem>
              {kategoriHarga.map((c) => <SelectItem key={c.kode} value={c.kode}>{labelDenganKode(kategoriHarga, c.kode)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-32"><SelectValue placeholder="Tipe" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Semua Tipe</SelectItem>
              {ITEM_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <PalangDaftar daftar={daftar} placeholder="Cari kategori / tipe / bahan…" testId="pli-palang"
                      ekspor={{ nama: "master_daftar_harga", kolom: KOLOM_CSV }} />
        {loading ? <div className="py-8 text-center text-sm text-muted-foreground">Memuat...</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground text-xs uppercase">
                <tr>
                  <th className="text-left px-3 py-2">Price Group</th>
                  <ThUrut daftar={daftar} kunci="package_category" className="text-left px-3 py-2">Kategori</ThUrut>
                  <ThUrut daftar={daftar} kunci="item_type" className="text-left px-3 py-2">Tipe</ThUrut>
                  <ThUrut daftar={daftar} kunci="fabric" className="text-left px-3 py-2">Bahan</ThUrut>
                  {/* MIN-MAX-QTY-TANPA-ARTI-HARGA-01: satu kolom dua arti (BUSINESS-07/08) — kategori berjenjang (EVENT)
                      = pita harga dari–sampai; kategori lain = minimum pesanan, tanpa batas atas. */}
                  <ThUrut daftar={daftar} kunci="min_qty" className="text-right px-3 py-2" align="right">Minimum / jenjang dari</ThUrut>
                  <ThUrut daftar={daftar} kunci="max_qty" className="text-right px-3 py-2" align="right">Jenjang sampai</ThUrut>
                  <ThUrut daftar={daftar} kunci="unit_price_idr" className="text-right px-3 py-2" align="right">Harga Satuan (IDR)</ThUrut>
                  <ThUrut daftar={daftar} kunci="active" className="text-left px-3 py-2">Status</ThUrut>
                  {bolehTulis && <th className="px-3 py-2"></th>}
                </tr>
              </thead>
              <tbody>
                {daftar.total === 0 && <tr><td colSpan={bolehTulis ? 9 : 8} className="px-3 py-8 text-center text-muted-foreground">
                  {daftar.cari.trim() ? "Tidak ada yang cocok." : "Tidak ada item."}
                </td></tr>}
                {daftar.tampil.map((it) => (
                  <tr key={it.id} className="border-t border-border" data-testid={`pli-row-${it.id}`}>
                    <td className="px-3 py-2 font-mono text-xs">{pgName(it.price_group_id)}</td>
                    <td className="px-3 py-2"><Badge variant="outline" title={it.package_category}>{labelDenganKode(kategoriHarga, it.package_category)}</Badge></td>
                    <td className="px-3 py-2">{LABEL_TIPE[it.item_type] || it.item_type}</td>
                    <td className="px-3 py-2 text-muted-foreground">{it.fabric || "—"}</td>
                    <td className="px-3 py-2 text-right">{it.min_qty}</td>
                    <td className="px-3 py-2 text-right">{kategoriHarga.find((k) => k.kode === it.package_category)?.berjenjang ? (it.max_qty ?? "∞") : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono">{formatIDR(it.unit_price_idr)}</td>
                    <td className="px-3 py-2">{it.active ? <StatusBadge tone="success">Aktif</StatusBadge> : <Badge variant="outline">Nonaktif</Badge>}</td>
                    {bolehTulis && (
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {bolehTulisGroup(groups.find((g) => g.id === it.price_group_id)) ? (<>
                          <Button variant="ghost" size="icon" onClick={() => { setEditing(it); setShowForm(true); }} data-testid={`pli-edit-${it.id}`}><Edit size={14} /></Button>
                          <Button variant="ghost" size="icon" onClick={() => setConfirmDel(it)} className="text-danger-soft-foreground" data-testid={`pli-delete-${it.id}`}><Trash2 size={14} /></Button>
                        </>) : <span className="text-xs text-muted-foreground">cabang lain</span>}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            <Paginasi daftar={daftar} testId="pli-paginasi" />
          </div>
        )}
      </Card>

      {showForm && (
        <ItemFormModal item={editing} groups={groups.filter(bolehTulisGroup)} onClose={() => { setShowForm(false); setEditing(null); }} onSaved={() => { setShowForm(false); setEditing(null); load(); }} />
      )}

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => { if (!o) setConfirmDel(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Item?</AlertDialogTitle>
            <AlertDialogDescription>Item harga ini akan dihapus permanen. FA yang sudah ada tidak terpengaruh, tapi FA baru tidak akan bisa memakai harga ini.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="pli-delete-confirm">Hapus</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ItemFormModal({ item, groups, onClose, onSaved }) {
  const kategoriHarga = useKategoriHarga();
  const [form, setForm] = useState({
    price_group_id: item?.price_group_id || "",
    package_category: item?.package_category || "",
    package_name: item?.package_name || "",
    item_type: item?.item_type || ITEM_TYPES[0],
    fabric: item?.fabric || "",
    min_qty: item?.min_qty ?? 6,
    max_qty: item?.max_qty ?? "",
    unit_price_idr: item?.unit_price_idr ?? 0,
    effective_from: item?.effective_from || "",
    effective_to: item?.effective_to || "",
    active: item?.active ?? true,
    notes: item?.notes || "",
  });
  useEffect(() => {
    if (item || !kategoriHarga.length) return;
    setForm((f) => (f.package_category ? f : { ...f, package_category: kodeBawaan(kategoriHarga, ""), min_qty: kategoriHarga[0].minimum_pesanan ?? f.min_qty }));
  }, [item, kategoriHarga]);
  // BUSINESS-07/08: kolom min/max berarti "jenjang harga" hanya untuk kategori berjenjang (EVENT); selain itu
  // min_qty = minimum pesanan dan batas atas tidak dipakai (dikirim kosong).
  const berjenjang = !!kategoriHarga.find((k) => k.kode === form.package_category)?.berjenjang;
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.price_group_id || !form.package_category || !form.item_type) { toast.error("Isian wajib belum lengkap"); return; }
    setSaving(true);
    try {
      const body = {
        ...form,
        min_qty: parseInt(form.min_qty) || 0,
        max_qty: !berjenjang || form.max_qty === "" ? null : parseInt(form.max_qty),
        unit_price_idr: parseInt(form.unit_price_idr) || 0,
        effective_from: form.effective_from || null,
        effective_to: form.effective_to || null,
        fabric: form.fabric || null,
        notes: form.notes || null,
        package_name: form.package_name || null,
      };
      let hasil;
      if (item) {
        delete body.price_group_id;
        hasil = await api.patch(`/master/price-list-items/${item.id}`, body);
      } else {
        hasil = await api.post("/master/price-list-items", body);
      }
      toast.success("Item disimpan");
      // Pita EVENT (BUSINESS-07, 22 Sep 2026): peringatan terang dari pagar tulis —
      // mis. belum ada pita tanpa batas atas — bukan galat, tidak menahan simpan.
      (hasil?.data?.peringatan || []).forEach((p) => toast.warning(p, { duration: 9000 }));
      onSaved();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Item gagal disimpan"));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="pli-form-modal">
        <DialogHeader><DialogTitle>{item ? "Edit Item" : "Tambah Item"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div className="col-span-2">
            <Label>Price Group *</Label>
            <Select value={form.price_group_id} onValueChange={(v) => upd("price_group_id", v)} disabled={!!item}>
              <SelectTrigger data-testid="pli-form-pg"><SelectValue placeholder="Pilih price group" /></SelectTrigger>
              <SelectContent>{groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.code} — {g.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Kategori Harga *</Label>
            <Select value={form.package_category} onValueChange={(v) => setForm((f) => ({ ...f, package_category: v, min_qty: item ? f.min_qty : (kategoriHarga.find((k) => k.kode === v)?.minimum_pesanan ?? f.min_qty) }))}>
              <SelectTrigger data-testid="pli-form-cat"><SelectValue placeholder="— pilih kategori —" /></SelectTrigger>
              <SelectContent>{kategoriHarga.map((c) => <SelectItem key={c.kode} value={c.kode}>{c.label} ({c.kode})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Nama Paket</Label>
            <Input value={form.package_name} onChange={(e) => upd("package_name", e.target.value)} placeholder="cth: Budget Top Premium" />
          </div>
          <div>
            <Label>Tipe Item *</Label>
            <Select value={form.item_type} onValueChange={(v) => upd("item_type", v)}>
              <SelectTrigger data-testid="pli-form-type"><SelectValue /></SelectTrigger>
              <SelectContent>{ITEM_TYPES.map((t) => <SelectItem key={t} value={t}>{LABEL_TIPE[t]} ({t})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Bahan</Label>
            <Input value={form.fabric} onChange={(e) => upd("fabric", e.target.value)} placeholder="cth: drifit, polycotton" />
          </div>
          <div>
            <Label>{berjenjang ? "Jenjang dari (lembar) *" : "Minimum pesanan (lembar) *"}</Label>
            <Input type="number" min={0} value={form.min_qty} onChange={(e) => upd("min_qty", e.target.value)} data-testid="pli-form-minqty" />
          </div>
          {berjenjang && (
            <div>
              <Label>Jenjang sampai (lembar; kosongkan = tanpa batas)</Label>
              <Input type="number" min={0} value={form.max_qty} onChange={(e) => upd("max_qty", e.target.value)} />
            </div>
          )}
          <div>
            <Label>Harga Satuan (IDR) *</Label>
            <Input type="number" min={0} value={form.unit_price_idr} onChange={(e) => upd("unit_price_idr", e.target.value)} data-testid="pli-form-price" />
          </div>
          <div>
            <Label>Berlaku Dari</Label>
            <Input type="date" value={form.effective_from} onChange={(e) => upd("effective_from", e.target.value)} />
          </div>
          <div>
            <Label>Berlaku Sampai</Label>
            <Input type="date" value={form.effective_to} onChange={(e) => upd("effective_to", e.target.value)} />
          </div>
          <div className="col-span-2 flex items-center gap-2">
            <Checkbox checked={form.active} onCheckedChange={(v) => upd("active", !!v)} id="pliactive" />
            <Label htmlFor="pliactive" className="cursor-pointer">Aktif</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={save} disabled={saving} data-testid="pli-form-save">{saving ? "Menyimpan..." : "Simpan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
